import { useState } from "react";
import { useTranslation } from "react-i18next";
import MainLayout from "@/components/Layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Download, Trash2, Cookie } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { invokeFn, getErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import GDPRExportPDF, { GDPRExportData } from "@/pdf/templates/GDPRExportPDF";

const PrivacyData = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      // invokeFn throws on a transport error AND on a 200-with-error body, so we
      // never feed a failed/partial response into the PDF and claim success.
      const data = await invokeFn<GDPRExportData>('export-user-data', { userId: user?.id });

      const exportData = data;
      const blob = await pdf(<GDPRExportPDF data={exportData} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: t('settings.dataExported'),
        description: t('settings.dataExportedDesc'),
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('settings.exportFailed'),
        description: getErrorMessage(error, t('settings.exportFailedDesc')),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // invokeFn throws on a transport error AND on a 200-with-error body, so we
      // never log the user out / claim deletion when the account was NOT deleted.
      await invokeFn('delete-user-account', { userId: user?.id });

      toast({
        title: t('settings.accountDeleted'),
        description: t('settings.accountDeletedDesc'),
      });

      await logout();
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('settings.deletionFailed'),
        description: getErrorMessage(error, t('settings.deletionFailedDesc')),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearCookies = async () => {
    try {
      // Clear localStorage
      localStorage.removeItem('gdpr-consent');

      // Clear database record if user is authenticated
      if (user) {
        const { error } = await supabase
          .from('user_consent')
          .delete()
          .eq('user_id', user.id);

        if (error) {
          console.error('Error clearing consent from database:', error);
        }
      }

      toast({
        title: t('settings.cookiePrefsCleared'),
        description: t('settings.cookiePrefsClearedDesc'),
      });
    } catch (error) {
      console.error('Error clearing cookies:', error);
      toast({
        title: t('common.errorTitle'),
        description: t('settings.cookiePrefsClearFailed'),
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout title={t('settings.privacyAndData')}>
      <div className="container max-w-4xl py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">{t('settings.privacyAndData')}</h1>
          <p className="text-muted-foreground">{t('settings.managePrivacySettings')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              {t('settings.exportYourData')}
            </CardTitle>
            <CardDescription>
              {t('settings.exportDataDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.exportDataBody')}
            </p>
            <Button onClick={handleExportData} disabled={isExporting}>
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? t('settings.exporting') : t('settings.exportMyData')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5" />
              {t('settings.cookiePreferences')}
            </CardTitle>
            <CardDescription>
              {t('settings.cookiePrefsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.cookiePrefsBody')}
            </p>
            <Button onClick={handleClearCookies} variant="outline">
              <Cookie className="mr-2 h-4 w-4" />
              {t('settings.resetCookiePrefs')}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              {t('settings.deleteAccount')}
            </CardTitle>
            <CardDescription>
              {t('settings.deleteAccountDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.deleteAccountBody')}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.deleteMyAccount')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('settings.deleteConfirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('settings.deleteConfirmDesc')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? t('settings.deleting') : t('settings.deleteAccount')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PrivacyData;
